import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Button, InputField, Modal } from '../../../../../components'
import SelectField from '../../../../../components/Form/Select'
import { getOptions } from '../../utils/index'
import { updateJobInfo, addRevision } from '../../api/index'
import {
    HIDE_LOADER,
    SHOW_LOADER,
} from '../../../../../redux/slices/LoaderSlice'
import { useAppDispatch } from '../../../../../redux/hook'
import { SHOW_NOTIFICATION } from '../../../../../redux/slices/showNotificationSlice'
import { updateJobInformation, addRevisedJobInfo } from '../../../slice/index'
import { updateJobInfoTypes, addRevisionTypes } from '../../api/type'
import './index.css'
import { convertDateFormat } from '../../../../holidays/utils'

interface AddModalProps {
    show: boolean
    onClose: (value: boolean) => void
    jobInfo?: any
    addCase?: string
    lookupValues?: any
    userId?: string
}

interface addRevisionFormValues {
    jobTitle: string
    employmentType: string
    salaryPerHour: string
    hoursAllocated: string
    earnsCommission: string
    commissionAmount: string
    earnsBonus: string
    bonusAmount: string
    insurableEarnings: string
    payFrequency: string
    revisionAsOn?: string
}

const AddRevision = ({
    show,
    onClose,
    jobInfo,
    addCase,
    lookupValues,
    userId,
}: AddModalProps) => {
    const dispatch = useAppDispatch()
    const employmentTypeOptions = getOptions(lookupValues, 'EmploymentType')
    const payFrequencyOptions = getOptions(lookupValues, 'PayFrequency')

    const validationSchema = Yup.object().shape({
        jobTitle: Yup.string().required('Job title is required'),
        employmentType: Yup.string().required('Employment type is required'),
        earnsBonus: Yup.string().required('Employee earns bonus is required'),
        payFrequency: Yup.string().required('Pay frequency is required'),
    })

    return (
        <Modal
            show={show}
            handleClose={() => onClose(false)}
            title={addCase === 'Add' ? 'Add New Revision' : 'Edit Info'}
            customWidth={true}
            body={
                <Formik
                    initialValues={{
                        jobTitle: addCase === 'Edit' ? jobInfo?.jobTitle : '',
                        employmentType:
                            addCase === 'Edit'
                                ? jobInfo?.employmentTypeId
                                : employmentTypeOptions[0]?.value,
                        salaryPerHour:
                            addCase === 'Edit' &&
                            jobInfo?.salaryOrHourlyRate !== null
                                ? jobInfo?.salaryOrHourlyRate
                                : '',
                        hoursAllocated:
                            addCase === 'Edit' &&
                            jobInfo?.hoursAllocated !== null
                                ? jobInfo?.hoursAllocated
                                : '',
                        earnsCommission:
                            addCase === 'Edit'
                                ? jobInfo?.employeeEarnsCommision
                                    ? 'Yes'
                                    : 'No'
                                : 'Yes',
                        commissionAmount:
                            addCase === 'Edit' &&
                            jobInfo?.commissionAmout !== null
                                ? jobInfo?.commissionAmout
                                : '',
                        earnsBonus:
                            addCase === 'Edit'
                                ? jobInfo?.employeeEarnsBonus
                                    ? 'Yes'
                                    : 'No'
                                : 'Yes',
                        bonusAmount:
                            addCase === 'Edit' && jobInfo?.bonusAmount !== null
                                ? jobInfo?.bonusAmount
                                : '',
                        insurableEarnings:
                            addCase === 'Edit' &&
                            jobInfo?.yearlyInsurableEarning !== null
                                ? jobInfo?.yearlyInsurableEarning
                                : '',
                        payFrequency:
                            addCase === 'Edit'
                                ? jobInfo?.payFrequencyId
                                : payFrequencyOptions[0]?.value,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (
                        values,
                        actions: FormikHelpers<addRevisionFormValues>
                    ) => {
                        try {
                            dispatch(SHOW_LOADER())
                            const requestBody = {
                                ...(addCase === 'Add'
                                    ? { currentJobId: jobInfo?.jobId }
                                    : { jobId: jobInfo?.jobId }),
                                jobTitle: values.jobTitle,
                                employmentTypeId: parseInt(
                                    values.employmentType
                                ),
                                salaryOrHourlyRate: values.salaryPerHour
                                    ? parseFloat(values.salaryPerHour)
                                    : null,
                                hoursAllocated: values.hoursAllocated
                                    ? parseFloat(values.hoursAllocated)
                                    : null,
                                employeeEarnsCommision:
                                    values.earnsCommission === 'Yes',
                                commissionAmout: values.commissionAmount
                                    ? parseFloat(values.commissionAmount)
                                    : null,
                                employeeEarnsBonus: values.earnsBonus === 'Yes',
                                bonusAmount: values.bonusAmount
                                    ? parseFloat(values.bonusAmount)
                                    : null,
                                yearlyInsurableEarning: values.insurableEarnings
                                    ? parseFloat(values.insurableEarnings)
                                    : null,
                                payFrequencyId: parseInt(values.payFrequency),
                                revisionAsOn: convertDateFormat(
                                    new Date() as unknown as string
                                ),
                            }

                            const response = await (addCase === 'Add'
                                ? addRevision({
                                      ...requestBody,
                                      userId: userId,
                                  } as addRevisionTypes)
                                : updateJobInfo(
                                      requestBody as updateJobInfoTypes
                                  ))

                            if (response?.status === 200) {
                                if (addCase === 'Edit') {
                                    dispatch(
                                        updateJobInformation(requestBody as any)
                                    )
                                } else {
                                    dispatch(
                                        addRevisedJobInfo(requestBody as any)
                                    )
                                }

                                dispatch(
                                    SHOW_NOTIFICATION({
                                        message:
                                            addCase === 'Edit'
                                                ? 'Revision updated successfully'
                                                : 'Revision added successfully',
                                        type: 'success',
                                    })
                                )
                            }
                        } catch (error) {
                            dispatch(
                                SHOW_NOTIFICATION({
                                    message: 'Something went wrong',
                                    type: 'error',
                                })
                            )
                            console.error('Update error:', error)
                        } finally {
                            dispatch(HIDE_LOADER())
                            onClose(false)
                            actions.setSubmitting(false)
                        }
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                    }) => (
                        <form onSubmit={handleSubmit} className="revised-form">
                            <div className="row mrg-top">
                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <InputField
                                            type="text"
                                            lable="Job Title"
                                            name="jobTitle"
                                            value={values.jobTitle}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors?.jobTitle as string}
                                            touched={
                                                touched?.jobTitle as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <SelectField
                                            name="employmentType"
                                            label="Employment Type"
                                            options={employmentTypeOptions}
                                            value={values.employmentType}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                errors?.employmentType as string
                                            }
                                            touched={
                                                touched?.employmentType as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <InputField
                                            type="text"
                                            lable="Salary/Hourly Rate"
                                            name="salaryPerHour"
                                            value={values.salaryPerHour}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                errors.salaryPerHour as string
                                            }
                                            touched={
                                                touched.salaryPerHour as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <InputField
                                            type="text"
                                            lable="Hours Allocated"
                                            name="hoursAllocated"
                                            value={values.hoursAllocated}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                errors.hoursAllocated as string
                                            }
                                            touched={
                                                touched.hoursAllocated as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <SelectField
                                            name="earnsCommission"
                                            label="Employee Earns Commission"
                                            options={[
                                                { value: 'Yes', label: 'Yes' },
                                                { value: 'No', label: 'No' },
                                            ]}
                                            value={values.earnsCommission}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.earnsCommission}
                                            touched={touched.earnsCommission}
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <InputField
                                            type="text"
                                            lable="Commission Amount"
                                            name="commissionAmount"
                                            value={values.commissionAmount}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                errors.commissionAmount as string
                                            }
                                            touched={
                                                touched.commissionAmount as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <SelectField
                                            name="earnsBonus"
                                            label="Employee Earns a Bonus"
                                            options={[
                                                { value: 'Yes', label: 'Yes' },
                                                { value: 'No', label: 'No' },
                                            ]}
                                            value={values.earnsBonus}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.earnsBonus}
                                            touched={touched.earnsBonus}
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <InputField
                                            type="text"
                                            lable="Bonus Amount"
                                            name="bonusAmount"
                                            value={values.bonusAmount}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.bonusAmount as string}
                                            touched={
                                                touched.bonusAmount as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <InputField
                                            type="text"
                                            lable="Yearly Insurable Earnings"
                                            name="insurableEarnings"
                                            value={values.insurableEarnings}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                errors.insurableEarnings as string
                                            }
                                            touched={
                                                touched.insurableEarnings as boolean
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group bg-green-color">
                                        <SelectField
                                            name="payFrequency"
                                            label="Pay Frequency"
                                            options={payFrequencyOptions}
                                            value={values.payFrequency}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                errors.payFrequency as string
                                            }
                                            touched={
                                                touched.payFrequency as boolean
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="footer-block">
                                <Button type="submit">
                                    {addCase === 'Add' ? 'SAVE' : 'UPDATE'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline-success"
                                    onClick={() => onClose(false)}
                                >
                                    CANCEL
                                </Button>
                            </div>
                        </form>
                    )}
                </Formik>
            }
            onSuccess={() => {
                onClose(false)
            }}
        />
    )
}
export default AddRevision
